'use client';

import { type FC, useState } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from '../AuctionsScreen/AuctionsScreen.translations';
import { ScreenContent, ScreenFixedBottom } from '../../screen';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, Text } from '../../components/ui';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { ItemCard } from '../../components/ItemCard';
import { AuctionStats } from '../../components/AuctionStats';
import { BidsList } from './BidsList';
import type { ScreenParams } from '../../types/navigation';
import { useProfile } from '../../queries/useProfileQueries';
import { useAuctionDetails } from './useAuctionDetails';
import { useAuctionBids } from './useAuctionBids';
import { useAuctionTimer } from './useAuctionTimer';
import { usePeriodicUpdate } from './usePeriodicUpdate';
import { useBidPlacement } from './useBidPlacement';
import { getMinimumBidAmount } from './utils';

interface AuctionDetailsScreenProps {
  params?: ScreenParams;
  onBack?: () => void;
}

export const AuctionDetailsScreen: FC<AuctionDetailsScreenProps> = ({ params }) => {
  const { t } = useLocalTranslation(translations);
  const { data: profile } = useProfile();
  const auctionId = params?.auctionId;
  const [bidSheetOpen, setBidSheetOpen] = useState(false);

  const {
    auction,
    loading,
    error,
    currentUserId,
    allBids,
    setAllBids,
    loadAuctionDetails,
  } = useAuctionDetails({ auctionId });

  const { loadingMoreBids, scrollRef } = useAuctionBids({
    auctionId,
    setAllBids,
  });

  const { localTimeRemaining } = useAuctionTimer({
    auction,
    onExpired: loadAuctionDetails,
  });

  usePeriodicUpdate({
    auction,
    localTimeRemaining,
    onUpdate: loadAuctionDetails,
  });

  const { bidAmount, setBidAmount, isPlacingBid, bidError, isLastBidderCurrentUser, handlePlaceBid } =
    useBidPlacement({
      auction,
      allBids,
      currentUserId,
      onBidPlaced: async () => {
        await loadAuctionDetails();
        setBidSheetOpen(false);
      },
    });

  if (loading) {
    return (
      <ScreenContent className="flex justify-center items-center min-h-[200px] text-white">
        <LoadingSpinner />
      </ScreenContent>
    );
  }

  if (error || !auction) {
    return (
      <ScreenContent>
        <ErrorMessage
          error={error || t('errorsNotFound')}
          message={error || t('errorsNotFound')}
          onRetry={loadAuctionDetails}
        />
      </ScreenContent>
    );
  }

  const isActive = localTimeRemaining > 0;

  return (
    <>
      {isActive && (
        <ScreenFixedBottom>
          <div className="px-4 pb-[calc(16px+var(--tg-viewport-safe-area-inset-bottom,0px))] pt-3">
            <Button
              size="l"
              stretched
              onClick={() => setBidSheetOpen(true)}
              disabled={isLastBidderCurrentUser}
            >
              {isLastBidderCurrentUser ? t('lastBidIsYours') : t('placeBid')}
            </Button>
          </div>
        </ScreenFixedBottom>
      )}
      <ScreenContent className="flex flex-col gap-4 p-4" scrollRef={scrollRef}>
        <ItemCard
          imageUrl={auction.item.imageUrl || ''}
          name={auction.item.name}
          rarity={auction.item.rarityName || undefined}
          rarityColor={auction.item.rarityColor || undefined}
        />

        <AuctionStats currentBid={auction.currentBid} timeRemaining={localTimeRemaining} />

        {allBids.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="px-1 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
              {t('bidsTitle')}
            </div>
            <BidsList bids={allBids} currentUserId={currentUserId} />
            {loadingMoreBids && (
              <div className="flex items-center justify-center p-4">
                <LoadingSpinner size="small" />
              </div>
            )}
          </div>
        )}
      </ScreenContent>

      <BottomSheet
        isOpen={bidSheetOpen}
        onClose={() => setBidSheetOpen(false)}
        title={t('placeBid')}
        heightClass="h-auto max-h-[70vh]"
      >
        <div className="flex flex-col gap-3 pb-6 pt-1">
          <input
            type="number"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            placeholder={t('enterBidAmount')}
            aria-label={t('enterBidAmount')}
            autoFocus
            className="w-full rounded-xl bg-fill-tertiary px-4 py-3.5 text-center text-lg font-bold text-label-primary placeholder:text-base placeholder:font-normal placeholder:text-label-tertiary focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
            min={getMinimumBidAmount(auction)}
            max={profile?.balanceInCredits || 0}
          />
          {bidError && <Text className="text-center text-sm font-medium text-accent-red">{bidError}</Text>}
          <Button
            size="l"
            stretched
            onClick={handlePlaceBid}
            disabled={isPlacingBid || !bidAmount.trim() || isLastBidderCurrentUser}
          >
            {isPlacingBid ? t('placingBid') : t('placeBid')}
          </Button>
        </div>
      </BottomSheet>
    </>
  );
};
